/**
 * F-508-FE-01: AdminLoginPage
 *
 * Admin-only login at /admin/login.
 * - Email + password fields
 * - Calls POST /admin/auth/login via AuthContext.loginAdmin
 * - Force password change flow on first login (forcePasswordChange flag)
 * - POST /admin/auth/change-password for first-login password reset
 * - Completely separate from doctor login (no hospital selection)
 *
 * Design: Dark charcoal background, white card, clinical editorial aesthetic.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function ShieldIcon() {
  return (
    <svg
      className="w-8 h-8"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
      />
    </svg>
  );
}

function EyeIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Change Password Form (first-login flow)
// ---------------------------------------------------------------------------

interface ChangePasswordFormProps {
  email: string;
  tempToken: string;
  onSuccess: () => void;
}

function ChangePasswordForm({ email: _email, tempToken: _tempToken, onSuccess }: ChangePasswordFormProps) {
  // Note: email and tempToken props are unused — kept for interface stability.
  // The backend route uses the Bearer token (set in AuthContext) for identity
  // and bcrypt-compares currentPassword against the stored hash.
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const validate = (): string | null => {
    if (!currentPassword) return 'Please enter your current password.';
    if (newPassword.length < 12) return 'Password must be at least 12 characters.';
    if (!/[A-Z]/.test(newPassword)) return 'Password must contain at least one uppercase letter.';
    if (!/[0-9]/.test(newPassword)) return 'Password must contain at least one number.';
    if (newPassword !== confirmPassword) return 'Passwords do not match.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await api.post('/admin/auth/change-password', {
        currentPassword,
        newPassword,
      });
      onSuccess();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change password.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-clinical-amber/10 mx-auto mb-4">
          <svg className="w-7 h-7 text-clinical-amber" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
        </div>
        <h2 className="font-heading text-2xl font-semibold text-clinical-charcoal text-center">
          Set New Password
        </h2>
        <p className="text-sm font-body text-clinical-text-muted text-center mt-2">
          Your account requires a password change before you can continue.
        </p>
      </div>

      <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
        {/* Current password */}
        <div>
          <label htmlFor="current-password" className="form-label">Current Password</label>
          <div className="relative">
            <input
              id="current-password"
              type={showCurrent ? 'text' : 'password'}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); setError(''); }}
              className="form-input pe-10"
              placeholder="Your current password"
              required
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              className="absolute inset-y-0 end-0 flex items-center px-3 text-clinical-text-muted hover:text-clinical-charcoal transition-colors"
              aria-label={showCurrent ? 'Hide password' : 'Show password'}
            >
              <EyeIcon visible={showCurrent} />
            </button>
          </div>
        </div>

        {/* New password */}
        <div>
          <label htmlFor="new-password" className="form-label">New Password</label>
          <div className="relative">
            <input
              id="new-password"
              type={showNew ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
              className="form-input pe-10"
              placeholder="At least 12 characters"
              required
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute inset-y-0 end-0 flex items-center px-3 text-clinical-text-muted hover:text-clinical-charcoal transition-colors"
              aria-label={showNew ? 'Hide password' : 'Show password'}
            >
              <EyeIcon visible={showNew} />
            </button>
          </div>
          <p className="text-xs font-body text-clinical-text-muted mt-1">
            Min 12 chars, one uppercase, one number.
          </p>
        </div>

        {/* Confirm password */}
        <div>
          <label htmlFor="confirm-password" className="form-label">Confirm Password</label>
          <div className="relative">
            <input
              id="confirm-password"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              className="form-input pe-10"
              placeholder="Repeat new password"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute inset-y-0 end-0 flex items-center px-3 text-clinical-text-muted hover:text-clinical-charcoal transition-colors"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              <EyeIcon visible={showConfirm} />
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm font-body text-red-700">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full mt-2"
        >
          {isSubmitting ? 'Updating…' : 'Set Password & Continue'}
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Login Form
// ---------------------------------------------------------------------------

interface LoginFormProps {
  onForcePasswordChange: (email: string, tempToken: string) => void;
  onLoginSuccess: () => void;
}

function LoginForm({ onForcePasswordChange, onLoginSuccess }: LoginFormProps) {
  const { loginAdmin } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || !password) return;

      setIsSubmitting(true);
      setError('');

      try {
        // loginAdmin returns a discriminated result — branch on each case.
        const result = await loginAdmin(email.trim(), password);

        if (result.requirePasswordChange && result.tempToken) {
          // Force password change — show the change-password form in-page.
          onForcePasswordChange(email.trim(), result.tempToken);
          return;
        }

        if (result.mfaRequired && result.mfaToken) {
          // MFA verification required — navigate to MFA verify page.
          void navigate('/admin/mfa/verify', {
            state: { mfaToken: result.mfaToken, adminEmail: result.adminEmail },
          });
          return;
        }

        if (result.mfaSetupRequired && result.setupToken) {
          // MFA enrollment required — navigate to MFA setup page.
          void navigate('/admin/mfa/setup', {
            state: { setupToken: result.setupToken, adminEmail: result.adminEmail },
          });
          return;
        }

        // Normal success (including mfaSetupRecommended which doesn't block login).
        onLoginSuccess();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Login failed. Please try again.';
        setError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, password, loginAdmin, navigate, onForcePasswordChange, onLoginSuccess],
  );

  return (
    <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-5">
      {/* Email */}
      <div>
        <label htmlFor="admin-email" className="form-label">
          Email Address
        </label>
        <input
          id="admin-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); }}
          className="form-input"
          placeholder="admin@example.com"
          required
          autoFocus
        />
      </div>

      {/* Password */}
      <div>
        <label htmlFor="admin-password" className="form-label">
          Password
        </label>
        <div className="relative">
          <input
            id="admin-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            className="form-input pe-10"
            placeholder="Enter your password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 end-0 flex items-center px-3 text-clinical-text-muted hover:text-clinical-charcoal transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <EyeIcon visible={showPassword} />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm font-body text-red-700">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting || !email.trim() || !password}
        className="btn-primary w-full"
      >
        {isSubmitting ? 'Signing in…' : 'Sign In'}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type PageStep = 'login' | 'change-password';

export function AdminLoginPage() {
  const { isAuthenticated, isLoading, role } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<PageStep>('login');
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingTempToken, setPendingTempToken] = useState('');

  // Redirect if already authenticated as admin — but ONLY when we are on the
  // 'login' step. If step='change-password', the user holds a limited-scope
  // token and isAuthenticated may be false; the redirect must not fire.
  useEffect(() => {
    if (!isLoading && isAuthenticated && role === 'admin' && step === 'login') {
      void navigate('/admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, role, step, navigate]);

  const handleForcePasswordChange = (email: string, tempToken: string) => {
    setPendingEmail(email);
    setPendingTempToken(tempToken);
    setStep('change-password');
  };

  // Called by LoginForm on a clean successful login (no password change required).
  const handleLoginSuccess = () => {
    void navigate('/admin/dashboard', { replace: true });
  };

  const handlePasswordChangeSuccess = () => {
    // Password changed successfully on the backend. Reset the local step
    // machine BEFORE navigating so AdminLoginPage renders the login form
    // (not the change-password form) when the user lands on /admin/login.
    // Without resetSep('login'), the page would re-render the change-password
    // form indefinitely because component state persists across same-route
    // navigation.
    // The user must log in again with their new password — the limited-scope
    // token in AuthContext will be overwritten on successful re-login.
    setPendingEmail('');
    setPendingTempToken('');
    setStep('login');
    void navigate('/admin/login', { replace: true });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-charcoal">
        <div className="w-8 h-8 border-2 border-clinical-amber border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-clinical-charcoal px-4"
      style={{ backgroundImage: 'radial-gradient(ellipse at 50% 0%, rgba(212,165,116,0.08) 0%, transparent 70%)' }}
    >
      <div className="w-full max-w-sm">
        {/* Logo / Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 text-clinical-amber mb-4">
            <ShieldIcon />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-white">
            Clinical Interpreter
          </h1>
          <p className="text-sm font-body text-white/50 mt-1 uppercase tracking-widest">
            Admin Portal
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-clinical-lg p-8">
          {step === 'login' ? (
            <>
              <h2 className="font-heading text-xl font-semibold text-clinical-charcoal mb-6">
                Sign in to continue
              </h2>
              <LoginForm
                onForcePasswordChange={handleForcePasswordChange}
                onLoginSuccess={handleLoginSuccess}
              />
            </>
          ) : (
            <ChangePasswordForm
              email={pendingEmail}
              tempToken={pendingTempToken}
              onSuccess={handlePasswordChangeSuccess}
            />
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs font-body text-white/30 mt-6">
          Platform-level access only. Not for clinical use.
        </p>
      </div>
    </div>
  );
}
